import { action, computed, makeObservable, observable, toJS } from 'mobx';
import { searchService, taskService } from 'config/ioc';
import { forEach, isArray, isEmpty, omitBy } from 'lodash';
import { formatDate } from 'utils/date';
import { t } from 'utils/i18n';
import { sapFullDateFormat } from 'constants/dateFormats';
import { addDays, isWithinInterval, subMonths } from 'date-fns';
import { FULFILLED, PENDING, REJECTED } from 'constants/actions';
import { taskFilterTypes, taskStatuses } from 'constants/tasks';
import { getObjectFilter } from 'utils/filter';
import routerStore from './RouterStore';

class TaskListStore {
  tasksFetchState;

  addTaskFetchState;

  tasks = [];

  searchResults = [];

  prevScroll = null;

  newTask;

  refreshTaskList = false;

  filters = {
    type: null,
    period: null,
    status: null,
    author: null,
    assignees: null,
    constructions: null,
    customers: null,
    query: null,
  };

  constructor() {
    makeObservable(this, {
      tasksFetchState: observable,
      addTaskFetchState: observable,
      tasks: observable.ref,
      searchResults: observable.ref,
      newTask: observable.ref,
      refreshTaskList: observable,
      filters: observable.ref,
      setRefreshTask: action,
      setNewTask: action,
      getTasks: action,
      completeTask: action,
      closeTask: action,
      addTask: action,
      overdueTasks: computed,
      activeTasks: computed,
      completedTasks: computed,
      updateFilterProperty: action.bound,
      getFilterValue: action.bound,
      updateFilterObject: action.bound,
      resetAllFilters: action.bound,
      getFilteredTasks: action.bound,
      filteredTasks: computed,
      filtersBy: computed,
      getActiveTaskFilter: action.bound,
      getOverdueTaskFilter: action.bound,
      getCompletedTaskFilter: action.bound,
      queryParamsFilterObj: computed,
    });
  }

  get overdueTasks() {
    if (this.tasks.length === 0) return [];
    const filter = getObjectFilter(this.getOverdueTaskFilter());
    return this.getFilteredTasks(this.tasks, filter);
  }

  get activeTasks() {
    if (this.tasks.length === 0) return [];
    const filter = getObjectFilter(this.getActiveTaskFilter());
    return this.getFilteredTasks(this.tasks, filter);
  }

  get completedTasks() {
    if (this.tasks.length === 0) return [];
    const filter = getObjectFilter(this.getCompletedTaskFilter());
    return this.getFilteredTasks(this.tasks, filter);
  }

  get filteredTasks() {
    const filteredFiltersBy = omitBy(this.filtersBy, isEmpty);
    if (isEmpty(filteredFiltersBy)) {
      return this.tasks;
    }

    return this.getFilteredTasks(this.tasks, filteredFiltersBy);
  }

  get filtersBy() {
    return toJS(this.filters);
  }

  get queryParamsFilterObj() {
    let filters = {};
    forEach(this.filters, (filterValue, key) => {
      if (!filterValue || filterValue.length === 0) {
        filters = { ...filters, [key]: filterValue };
        return;
      }
      let newValue = filterValue;
      if (key === 'period') {
        newValue = {
          start: filterValue[0].start,
          end: filterValue[0].end,
          id: filterValue[0].id,
        };
      } else if (key !== 'query') {
        newValue = filterValue.map((value) => value.id);
      }
      filters = { ...filters, [key]: newValue };
    });
    return filters;
  }

  setRefreshTask(value) {
    this.refreshTaskList = value;
  }

  setNewTask(value) {
    this.newTask = value;
  }

  async getTasks() {
    this.tasksFetchState = PENDING;

    try {
      const response = await taskService.getTasks();
      this.tasks = response.data.data.items;
      this.tasksFetchState = FULFILLED;
    } catch (e) {
      this.tasksFetchState = REJECTED;
    }
  }

  async completeTask(taskId, resultTask) {
    this.fetchState = PENDING;

    try {
      await taskService.completeTask(taskId, resultTask);
      const response = await taskService.getTask(taskId);
      const [updatedTask] = response.data.data.items;

      this.tasks = this.tasks.map((task) => {
        if (task.id === taskId) {
          return updatedTask;
        }

        return task;
      });

      this.fetchState = FULFILLED;
    } catch (e) {
      this.fetchState = REJECTED;
    }
  }

  async closeTask(taskId) {
    this.fetchState = PENDING;

    try {
      await taskService.closeTask(taskId);
      const response = await taskService.getTask(taskId);
      const [updatedTask] = response.data.data.items;

      this.tasks = this.tasks.map((task) => {
        if (task.id === taskId) {
          return updatedTask;
        }

        return task;
      });

      this.fetchState = FULFILLED;
    } catch (e) {
      this.fetchState = REJECTED;
    }
  }

  redirectToReferrer = () => {
    const {
      location: { state },
    } = routerStore;
    const referrerUrl = (state && state.from) || '/organizer';

    routerStore.replace(referrerUrl);
  };

  async addTask(data) {
    this.addTaskFetchState = PENDING;
    try {
      const response = await taskService.addTask(data);
      const [task] = response.data.data.items;
      this.addTaskFetchState = FULFILLED;
      this.getTasks();
      this.newTask = task;
      this.redirectToReferrer();
    } catch (e) {
      this.addTaskFetchState = REJECTED;
    }
  }

  updateFilterProperty(id, item) {
    this.filters[id] = item;
  }

  getFilterValue(key, value, func) {
    value.forEach((item) => {
      if (item.id && item.name === undefined) {
        func({ query: item.id }).then((response) => {
          if (response.data.data.items.length) {
            const newItem = response.data.data.items[0];
            const newList = this.filters[key].map((x) =>
              x.id === item.id ? { ...x, ...newItem } : x,
            );
            this.updateFilterProperty(key, newList);
          }
        });
      }
    });
  }

  updateFilterObject(filters) {
    this.filters = filters;
    forEach(filters, (value, key) => {
      if (isEmpty(value)) return;
      if (key === 'author') {
        this.getFilterValue(key, value, (params) =>
          searchService.getUsers(params),
        );
      }
      if (key === 'assignees') {
        this.getFilterValue(key, value, (params) =>
          searchService.getUsers(params),
        );
      }
      if (key === 'customers') {
        this.getFilterValue(key, value, (params) =>
          searchService.getCustomers(params),
        );
      }
      if (key === 'constructions') {
        this.getFilterValue(key, value, (params) =>
          searchService.getConstructions(params),
        );
      }
      if (key === 'status') {
        const filterIds = value.map((filterValue) => filterValue.id);
        const newList = taskStatuses.filter((item) =>
          filterIds.includes(item.id),
        );
        this.updateFilterProperty(key, newList);
      }
      if (key === 'type') {
        const valuesToInt = value.map((item) =>
          typeof item.id === 'string' ? parseInt(item.id, 10) : item.id,
        );
        const newList = taskFilterTypes.filter((item) =>
          valuesToInt.includes(item.id),
        );
        this.updateFilterProperty(key, newList);
      }
      if (key === 'period') {
        const newValue = isArray(value) ? value : [value];
        const newList = newValue.map((item) => ({ ...item, name: t(item.id) }));
        this.updateFilterProperty(key, newList);
      }
    });
  }

  resetAllFilters() {
    Object.keys(this.filters).forEach((id) => {
      this.filters[id] = null;
    });
  }

  searchTasks(searchQuery) {
    this.searchResults = this.tasks.filter((task) => {
      return task.content.toLowerCase().includes(searchQuery);
    });
  }

  // eslint-disable-next-line class-methods-use-this
  getFilteredTasks(taskList, filterList) {
    let result = taskList;

    Object.entries(filterList).forEach((filter) => {
      const [filterName, filterValues] = filter;
      if (filterName === 'type') {
        const filterIds = filterValues.map((filterValue) => filterValue.id);
        result = result.filter((item) => {
          return filterIds.includes(item.type);
        });
      }

      if (filterName === 'query') {
        result = result.filter((task) => {
          return task.content.toLowerCase().includes(filterValues);
        });
      }

      if (filterName === 'status') {
        const filterIds = filterValues.map((filterValue) => filterValue.id);
        result = result.filter((item) => {
          return filterIds.includes(item.state);
        });
      }

      if (filterName === 'period') {
        const filterValue = isArray(filterValues)
          ? filterValues[0]
          : filterValues;
        if (!filterValue) return;
        result = result.filter((item) => {
          const due = new Date(item.due);
          const start = new Date(filterValue.start);
          const end = new Date(filterValue.end);

          return isWithinInterval(due, { start, end });
        });
      }

      if (filterName === 'author') {
        const filterIds = filterValues.map((filterValue) => filterValue.id);

        result = result.filter((item) => {
          return filterIds.some((filterId) => filterId === item.author.id);
        });
      }

      if (filterName === 'assignees') {
        const filterIds = filterValues.map((filterValue) => filterValue.id);

        result = result.filter((item) => {
          return filterIds.some((filterId) =>
            item.assignees.find((assignee) => filterId === assignee.id),
          );
        });
      }

      if (filterName === 'constructions') {
        const filterIds = filterValues.map((filterValue) => filterValue.id);

        result = result.filter((item) => {
          return filterIds.some(
            (filterId) => filterId === item.construction.id,
          );
        });
      }

      if (filterName === 'customers') {
        const filterIds = filterValues.map((filterValue) => filterValue.id);

        result = result.filter((item) => {
          return filterIds.some((filterId) => filterId === item.customer.id);
        });
      }
    });
    return result;
  }

  // eslint-disable-next-line class-methods-use-this
  getActiveTaskFilter() {
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    const end = addDays(start, 3);
    return {
      period: {
        id: 'threeDays',
        start: formatDate(start, sapFullDateFormat),
        end: formatDate(end, sapFullDateFormat),
      },
      status: ['opened'],
    };
  }

  // eslint-disable-next-line class-methods-use-this
  getOverdueTaskFilter() {
    const end = new Date();
    end.setHours(0, 0, 0, 0);
    const start = subMonths(end, 1);
    return {
      period: {
        id: 'lastMonth',
        start: formatDate(start, sapFullDateFormat),
        end: formatDate(end, sapFullDateFormat),
      },
      status: ['opened'],
    };
  }

  // eslint-disable-next-line class-methods-use-this
  getCompletedTaskFilter() {
    return {
      status: ['completed'],
      period: undefined,
    };
  }
}

const taskListStore = new TaskListStore();

export default taskListStore;
